import { Flow, Group, LogicalSpace, Obstacle } from '@thive/fddm-types';

import {
  isHighwayArea,
  isRackingArea,
  isSingularStationArea,
  isStationaryArea,
} from '@/modules/floorplanDesign/helpers/typeguards';
import { Area, Template } from '@/modules/floorplanDesign/types';

export type SelectedItems = {
  selectedAreas: Area[];
  selectedObstacles: Obstacle[];
  selectedTemplates: Template[];
  selectedLogicalSpaces: LogicalSpace[];
  selectedGroups: Group[];
  selectedFlows: Flow[];
};

export type SelectionHeaderState = {
  label: string;
  /** Only a single item can be renamed from the header */
  editable: boolean;
  canDuplicateMulti: boolean;
};

const canDuplicateArea = (area: Area): boolean =>
  isHighwayArea(area) ||
  isSingularStationArea(area) ||
  isStationaryArea(area) ||
  isRackingArea(area);

/**
 * @description The header for the selection: the item's (editable) name for a single item,
 * '[count] selected' for several. Null when nothing is selected.
 */
export const getSelectionHeaderState = ({
  selectedAreas,
  selectedObstacles,
  selectedTemplates,
  selectedLogicalSpaces,
  selectedGroups,
  selectedFlows,
}: SelectedItems): SelectionHeaderState | null => {
  const names = [
    ...selectedAreas.map(({ name }) => name),
    ...selectedObstacles.map(({ name }) => name),
    ...selectedTemplates.map(({ group }) => group.name || group.id),
    ...selectedLogicalSpaces.map(({ name }) => name),
    ...selectedGroups.map(({ name, id }) => name || id),
    ...selectedFlows.map(({ name }) => name),
  ];

  if (names.length === 0) return null;
  if (names.length > 1) {
    return { label: `${names.length} selected`, editable: false, canDuplicateMulti: false };
  }

  const [area] = selectedAreas;

  return {
    label: names[0] || 'Unknown',
    // A flow is renamed in its flow card
    editable: selectedFlows.length === 0,
    canDuplicateMulti: area !== undefined && canDuplicateArea(area),
  };
};
